import { useEffect, useState } from 'react';
import * as R from 'ramda';

import { Snackbar } from '@atom/mui';
import {
  AttributesType,
  HydratedAttributeGroupsItem,
} from '@atom/types/schema';
import { toggleFromSet } from '@atom/utilities/setUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AttributeDetailsContext, {
  AttributeDetailsAssetType,
  AttributeView,
  ErrorAttributes,
} from './AttributeDetailsContext';
import {
  getErrorAttributeIds,
  getInitialExpanded,
  getPreviousErrorAttributeIds,
  mapAttributeValue,
} from './attributeDetailUtilities';
import AttributeGroup from './AttributeGroup';

export type AttributeDetailsProps = {
  splitView?: boolean;
  view?: AttributeView;
  asset: AttributeDetailsAssetType;
  loading: string[];
  onAttributeUpdate: (any) => void;
  onPendingApproval?: (
    action: string,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => void;
  canManageChanges: boolean;
  canUpdate: boolean;
  // optional param to hard set the width of input fields
  inputWidth?: string;

  // workOrderAsset related props
  setUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
  // Skips triggering manual attribute validation and instead relies on passed
  // in errorAttributes and resolveInvalidAssetsError props
  skipManualAttributeValidation?: boolean;
  // errorAttributes can be passed if error state is handle outside this component
  errorAttributes?: ErrorAttributes;
  resolveInvalidAssetsError?: (
    assetId: string,
    attributeGroupId: string,
  ) => void;
};

const AttributeDetails = ({
  splitView = false,
  view = AttributeView.ASSET_DETAIL,
  asset,
  loading,
  onAttributeUpdate,
  onPendingApproval,
  canManageChanges,
  canUpdate,
  setUnsavedChanges = () => {},
  skipManualAttributeValidation,
  resolveInvalidAssetsError,
  inputWidth,
  ...props
}: AttributeDetailsProps) => {
  const [attributeValueMap, setAttributeValueMap] = useState<{
    [key: string]: any;
  }>();
  const [editGroup, setEditGroup] = useState<HydratedAttributeGroupsItem>();
  const [expanded, setExpanded] = useState<Set<string>>(
    getInitialExpanded(asset),
  );
  const [errorAttributes, setErrorAttributes] = useState<ErrorAttributes>(
    props?.errorAttributes,
  );

  useEffect(() => {
    setErrorAttributes(props?.errorAttributes);
  }, [props?.errorAttributes]);

  useEffect(() => {
    setUnsavedChanges(!isNilOrEmpty(attributeValueMap));
  }, [attributeValueMap]);

  const toggleExpanded = (id: string) => {
    setExpanded(toggleFromSet(expanded, id));
  };

  const onAttributeChange = (attributeId: string, value: any) => {
    if (!isNilOrEmpty(errorAttributes)) {
      const errorIds = getErrorAttributeIds(attributeValueMap, editGroup);
      setErrorAttributes(() => ({
        ...errorAttributes,
        [editGroup?.id]: errorIds,
      }));
    }

    setAttributeValueMap(current => ({
      ...current,
      [attributeId]: value,
    }));
  };

  const onCancel = () => {
    if (!skipManualAttributeValidation) {
      const errorIds = getPreviousErrorAttributeIds(editGroup);

      setErrorAttributes(() => ({
        ...errorAttributes,
        [editGroup?.id]: errorIds,
      }));
    }

    setAttributeValueMap(null);
    setEditGroup(null);
  };

  useEffect(() => {
    if (isNilOrEmpty(loading)) {
      setEditGroup(null);
      setAttributeValueMap(null);
    }
  }, [loading]);

  const onSubmit = () => {
    if (skipManualAttributeValidation) {
      resolveInvalidAssetsError(asset.id, editGroup.id);
    }

    if (!skipManualAttributeValidation) {
      const errorIds = getErrorAttributeIds(attributeValueMap, editGroup);

      if (errorIds.length > 0) {
        setErrorAttributes(() => ({
          ...errorAttributes,
          [editGroup?.id]: errorIds,
        }));

        Snackbar.error({
          message: 'Please fill out the required attributes.',
        });

        return;
      }
    }

    const updatedAttributes = R.keys(attributeValueMap).reduce(
      (acc, attributeId): any => {
        const attribute = editGroup.attributes.find(
          ({ id }) => id === attributeId,
        );

        return {
          ...acc,
          [attributeId]: {
            ...attribute,
            ...(view === AttributeView.MAP_TRAY
              ? {
                  newValue: mapAttributeValue(
                    attributeValueMap[attributeId],
                    attribute.dataType,
                  ),
                }
              : {
                  value: mapAttributeValue(
                    attributeValueMap[attributeId],
                    attribute.dataType,
                  ),
                }),
          },
        };
      },
      {},
    );

    const payload = {
      id: asset.id,
      attributeGroupName: editGroup?.name,
      attributes: updatedAttributes,
    };

    onAttributeUpdate(payload);
  };

  return (
    <AttributeDetailsContext.Provider
      value={{
        view,
        asset,
        loading,
        attributeValueMap,
        onCancel,
        onAttributeChange,
        editGroup,
        setEditGroup,
        expanded,
        toggleExpanded,
        onSubmit,
        onPendingApproval,
        canManageChanges,
        canUpdate,
        splitView,
        errorAttributes,
        setErrorAttributes,
        inputWidth,
      }}
    >
      {asset?.attributeGroups?.map((attributeGroup: any) => {
        return (
          <AttributeGroup
            key={attributeGroup.id}
            attributeGroup={attributeGroup}
          />
        );
      })}
    </AttributeDetailsContext.Provider>
  );
};

export default AttributeDetails;
